import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import WritingHandwritingPad from "../WritingHandwritingPad";
import WritingFormationDemo from "../WritingFormationDemo";
import { WRITING_ASSET } from "../writingAssets";
import { writingLocalStore } from "../writingLocalStore";
import { WRITING_L5, WRITING_L6, profileWritingName, sanitizedPracticeName, writingCapitalCue } from "../../../../data/writingCurriculum";
import {
  Board,
  ChoiceButton,
  ChoiceRow,
  CollectedStrip,
  GameFrame,
  IconButton,
  PlayIcon,
  RepeatDots,
  RoundBar,
  RoundCounter,
  ScorePill,
  SentenceFrame,
  SpeakerIcon,
  StickerTile,
  TargetChip,
  TimerPill,
  useCompleteOnce,
  useCountdown,
  useSpokenItem,
  useWrongFlash,
  withAlpha,
} from "./shared";

const PaintingPip = () => (
  <img
    src={WRITING_ASSET("paint_panda")}
    alt=""
    aria-hidden="true"
    draggable={false}
    onError={(e) => { e.currentTarget.style.display = "none"; }}
    style={{ position: "absolute", left: 8, bottom: 6, width: "calc(var(--tile, 72px) * 1.1)", height: "calc(var(--tile, 72px) * 1.1)", objectFit: "contain", pointerEvents: "none", filter: "drop-shadow(0 6px 10px rgba(11,26,51,0.18))" }}
  />
);

/**
 * Watch (optional) → trace N times → write M times from memory, per letter.
 * Shared by My Capital, Straight-Line Capitals and Curve Capitals.
 */
const TraceThenMemory = ({ letters, traces = 1, memories = 2, activity, speak, sfx, onComplete, onWrong, reduceMotion, watch = false, paintPanda = false }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState(watch ? "watch" : "trace");
  const [hits, setHits] = useState(0);
  const [take, setTake] = useState(0);
  const letter = letters[index];
  const need = phase === "trace" ? traces : memories;

  const accepted = () => {
    const nextHits = hits + 1;
    if (nextHits < need) {
      setHits(nextHits);
      speak?.(phase === "trace" ? `Lovely trace. Trace capital ${letter} again.` : `Great. Write capital ${letter} once more.`, { interrupt: true });
      return;
    }
    if (phase === "trace") {
      setPhase("memory");
      setHits(0);
      speak?.("That trace matched. Now write the capital from memory.", { interrupt: true });
      return;
    }
    if (index + 1 >= letters.length) finish();
    else {
      setIndex((n) => n + 1);
      setPhase(watch ? "watch" : "trace");
      setHits(0);
    }
  };

  const caption = phase === "watch" ? "Watch" : phase === "trace" ? "Trace" : "Write";

  return (
    <GameFrame>
      <RoundBar
        left={
          <TargetChip glyph={letter} caption={caption} accent={activity.accent}>
            {phase !== "watch" && <RepeatDots total={need} done={hits} accent={activity.accent} />}
          </TargetChip>
        }
        right={
          <>
            {watch && (
              <IconButton
                label="Watch Pip draw it again"
                onClick={() => { sfx?.("pop", { volume: 0.4 }); setTake((t) => t + 1); setPhase("watch"); setHits(0); }}
                accent={activity.accent}
              >
                <PlayIcon size={20} />
              </IconButton>
            )}
            {letters.length > 1 && <RoundCounter done={index} total={letters.length} accent={activity.accent} />}
          </>
        }
      />
      {phase === "watch" ? (
        <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
          <WritingFormationDemo
            key={`${letter}-${take}`}
            letter={letter}
            accent={activity.accent}
            reduceMotion={reduceMotion}
            onFinished={() => {
              // Under reduced motion the demo ends before the player's intro starts speaking.
              window.setTimeout(() => speak?.(`Capital ${letter}. Now trace it.`, { dedupe: true }), reduceMotion ? 800 : 0);
              window.setTimeout(() => setPhase("trace"), reduceMotion ? 100 : 500);
            }}
          />
          {paintPanda && <PaintingPip />}
        </div>
      ) : (
        <WritingHandwritingPad
          key={`${letter}-${phase}-${hits}`}
          target={letter}
          accent={activity.accent}
          showsGuide={phase === "trace"}
          sfx={sfx}
          reduceMotion={reduceMotion}
          onAccepted={accepted}
          onRejected={() => onWrong?.()}
        />
      )}
    </GameFrame>
  );
};

export const MyCapitalGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion, profile }) => {
  const letter = (profileWritingName(profile?.name || "")[0] || "A").toUpperCase();
  return (
    <TraceThenMemory
      letters={[letter]}
      traces={2}
      memories={3}
      watch
      activity={activity}
      speak={speak}
      sfx={sfx}
      onComplete={onComplete}
      onWrong={onWrong}
      reduceMotion={reduceMotion}
    />
  );
};

export const StraightLineCapitalsGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const letters = activity.group === "slanted" ? WRITING_L5.slantedCapitals : WRITING_L5.rightAngleCapitals;
  return (
    <TraceThenMemory
      letters={letters}
      traces={1}
      memories={2}
      activity={activity}
      speak={speak}
      sfx={sfx}
      onComplete={onComplete}
      onWrong={onWrong}
      reduceMotion={reduceMotion}
    />
  );
};

export const CurveCapitalsGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const letters = activity.group === "curveTwo" ? WRITING_L6.curveCapitalsTwo : WRITING_L6.curveCapitalsOne;
  return (
    <TraceThenMemory
      letters={letters}
      traces={1}
      memories={2}
      watch={!!activity.paintPanda}
      paintPanda={!!activity.paintPanda}
      activity={activity}
      speak={speak}
      sfx={sfx}
      onComplete={onComplete}
      onWrong={onWrong}
      reduceMotion={reduceMotion}
    />
  );
};

/* ── Capital From Memory: hear it, write it, no model ───────────────────── */
export const CapitalFromMemoryGame = ({ lesson, activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const rounds = lesson.lessonNumber === 6 ? WRITING_L6.memoryRounds : WRITING_L5.memoryRounds;
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [misses, setMisses] = useState(0);
  const letter = rounds[index];
  const cue = lesson.lessonNumber === 6
    ? `Listen: ${WRITING_L5.sounds[letter] || letter}. Write its capital.`
    : writingCapitalCue(letter);

  useSpokenItem(speak, cue);
  useEffect(() => {
    setMisses(0);
  }, [letter]);

  const revealed = misses >= 2;

  return (
    <GameFrame>
      <RoundBar
        left={
          <TargetChip glyph={letter} caption={revealed ? `Write capital ${letter}` : "Listen, then write"} accent={activity.accent} hidden={!revealed}>
            {!revealed && <span style={{ fontSize: 13, color: LEARN.color.muted, fontWeight: 600 }}>The letter is a secret until you try</span>}
          </TargetChip>
        }
        right={
          <>
            <IconButton label="Hear the letter again" onClick={() => speak?.(cue, { interrupt: true })} accent={activity.accent} filled>
              <SpeakerIcon size={20} />
            </IconButton>
            <RoundCounter done={index} total={rounds.length} accent={activity.accent} />
          </>
        }
      />
      <WritingHandwritingPad
        key={letter}
        target={letter}
        accent={activity.accent}
        showsGuide={false}
        hintAvailable={revealed}
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={() => (index + 1 >= rounds.length ? finish() : setIndex((n) => n + 1))}
        onRejected={() => {
          onWrong?.();
          setMisses((m) => m + 1);
          speak?.(misses + 1 >= 2 ? `Here it is: capital ${letter}. Try again.` : `Listen once more. ${cue}`, { interrupt: true });
        }}
      />
    </GameFrame>
  );
};

/* ── Big and Small: match capital to lowercase partner ──────────────────── */
export const BigAndSmallGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [remaining, setRemaining] = useState(WRITING_L5.casePairs);
  const [matched, setMatched] = useState([]);
  const [capital, setCapital] = useState(null);
  const { flash, isWrong } = useWrongFlash();

  const pickCapital = (value) => {
    sfx?.("pop", { volume: 0.5 });
    setCapital(value);
    speak?.(`Capital ${value}. Now find lowercase ${value.toLowerCase()}.`, { interrupt: true });
  };

  const pickLower = (lower) => {
    if (!capital) {
      speak?.("Tap a capital first.", { interrupt: true });
      flash(`lower-${lower}`);
      return;
    }
    const pair = remaining.find((p) => p.capital === capital);
    if (pair?.lowercase !== lower) {
      flash(`lower-${lower}`);
      onWrong?.();
      speak?.("Those two are different partners. Try another lowercase letter.", { interrupt: true });
      return;
    }
    sfx?.("snap");
    const next = remaining.filter((p) => p.capital !== capital);
    setMatched((m) => [...m, `${pair.capital}${pair.lowercase}`]);
    setCapital(null);
    setRemaining(next);
    speak?.(`${pair.capital} and ${pair.lowercase} are partners.`, { interrupt: true });
    if (!next.length) window.setTimeout(finish, reduceMotion ? 100 : 600);
  };

  const tileSize = "calc(var(--tile, 72px) * 0.82)";

  return (
    <GameFrame center>
      <Board
        title={activity.title}
        symbol={activity.symbol}
        accent={activity.accent}
        right={<RoundCounter done={matched.length} total={WRITING_L5.casePairs.length} accent={activity.accent} label="Pairs" />}
        caption={capital ? `Find lowercase ${capital.toLowerCase()}` : "Tap a capital"}
        bodyStyle={{ gap: 20 }}
      >
        <ChoiceRow gap={12}>
          {WRITING_L5.casePairs.map((pair) => {
            const done = !remaining.some((p) => p.capital === pair.capital);
            return (
              <ChoiceButton
                key={pair.capital}
                label={pair.capital}
                ariaLabel={`Capital ${pair.capital}`}
                accent={activity.accent}
                size={tileSize}
                selected={capital === pair.capital}
                correct={done}
                dim={done}
                disabled={done}
                onClick={() => pickCapital(pair.capital)}
                reduceMotion={reduceMotion}
              />
            );
          })}
        </ChoiceRow>
        <div style={{ height: 2, background: withAlpha(activity.accent, 0.25), borderRadius: 2, width: "70%" }} />
        <ChoiceRow gap={12}>
          {WRITING_L5.lowercaseDisplayOrder.map((lower) => {
            const done = !remaining.some((p) => p.lowercase === lower);
            return (
              <ChoiceButton
                key={lower}
                label={lower}
                ariaLabel={`Lowercase ${lower}`}
                accent={activity.accent}
                size={tileSize}
                wrong={isWrong(`lower-${lower}`)}
                correct={done}
                dim={done}
                disabled={done}
                onClick={() => pickLower(lower)}
                reduceMotion={reduceMotion}
              />
            );
          })}
        </ChoiceRow>
      </Board>
      <CollectedStrip items={matched} accent={activity.accent} reduceMotion={reduceMotion} empty="Matched pairs appear here" style={{ justifyContent: "center" }} />
    </GameFrame>
  );
};

/* ── Fast Capitals ──────────────────────────────────────────────────────── */
export const FastCapitalsGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion, profile }) => {
  const letters = WRITING_L5.rightAngleCapitals.concat(WRITING_L5.slantedCapitals);
  const finish = useCompleteOnce(onComplete);
  const storeKey = "pixley.learn.writing.lesson5.fastCapitals.best";
  const [written, setWritten] = useState([]);
  const [best] = useState(() => writingLocalStore.lastScore(storeKey, profile?.id));
  const writtenRef = useRef(written);
  writtenRef.current = written;
  const seconds = useCountdown(activity.seconds || 60, {
    onEnd: () => {
      writingLocalStore.recordBestScore(storeKey, profile?.id, writtenRef.current.length);
      finish();
    },
  });

  return (
    <GameFrame>
      <RoundBar
        left={<TimerPill seconds={seconds} accent={activity.accent} />}
        right={<ScorePill score={written.length} unit={written.length === 1 ? "capital" : "capitals"} best={best} accent={activity.accent} />}
      />
      <CollectedStrip items={written} accent={activity.accent} reduceMotion={reduceMotion} empty="Write any capital you know" />
      <WritingHandwritingPad
        key={written.length}
        target={letters[written.length % letters.length]}
        accent={activity.accent}
        showsGuide={false}
        hintAvailable={false}
        autoSubmit
        letters={letters}
        sfx={sfx}
        reduceMotion={reduceMotion}
        acceptDelay={reduceMotion ? 100 : 480}
        onAccepted={(matched) => {
          speak?.(`Capital ${matched}.`, { interrupt: true });
          setWritten((prev) => [...prev, matched]);
        }}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Pair Writing ───────────────────────────────────────────────────────── */
export const PairWritingGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("capital");
  const [pairs, setPairs] = useState([]);
  const lower = WRITING_L6.pairRounds[index];
  const capital = lower.toUpperCase();
  const target = phase === "capital" ? capital : lower;

  const accepted = () => {
    if (phase === "capital") {
      setPhase("lower");
      speak?.(`Capital ${capital} matched. Now write lowercase ${lower}.`, { interrupt: true });
      return;
    }
    setPairs((p) => [...p, `${capital}${lower}`]);
    if (index + 1 >= WRITING_L6.pairRounds.length) finish();
    else {
      setIndex((n) => n + 1);
      setPhase("capital");
    }
  };

  return (
    <GameFrame>
      <RoundBar
        left={<TargetChip glyph={target} caption={phase === "capital" ? "Write the capital" : "Write the lowercase"} accent={activity.accent} />}
        right={<RoundCounter done={index} total={WRITING_L6.pairRounds.length} accent={activity.accent} />}
      />
      <CollectedStrip items={pairs} accent={activity.accent} reduceMotion={reduceMotion} empty="Finished pairs appear here" />
      <WritingHandwritingPad
        key={`${lower}-${phase}`}
        target={target}
        accent={activity.accent}
        showsGuide={false}
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={accepted}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Big or Small? ──────────────────────────────────────────────────────── */
export const BigOrSmallGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [filled, setFilled] = useState("");
  const { flash, isWrong } = useWrongFlash();
  const round = WRITING_L6.choiceRounds[index];
  const upper = round.missingLetter.toUpperCase();
  const lowerChoice = round.missingLetter.toLowerCase();

  const pick = (value) => {
    if (filled) return;
    if (value !== upper) {
      flash(value);
      onWrong?.();
      speak?.(round.kind === "name" ? "A name starts with a capital. Try the big letter." : "A sentence starts with a capital. Try the big letter.", { interrupt: true });
      return;
    }
    sfx?.("correct");
    setFilled(upper);
    speak?.(`Yes. ${round.fragmentBefore}${upper}${round.fragmentAfter}`, { interrupt: true });
    window.setTimeout(() => {
      setFilled("");
      if (index + 1 >= WRITING_L6.choiceRounds.length) finish();
      else setIndex((n) => n + 1);
    }, reduceMotion ? 250 : 1100);
  };

  return (
    <GameFrame center>
      <Board
        title={activity.title}
        symbol={activity.symbol}
        accent={activity.accent}
        right={<RoundCounter done={index} total={WRITING_L6.choiceRounds.length} accent={activity.accent} />}
        caption={round.kind === "name" ? "A name" : "A sentence start"}
        bodyStyle={{ gap: 24 }}
      >
        <SentenceFrame key={index} before={round.fragmentBefore} after={round.fragmentAfter} filled={filled} blankWidth="1.1em" accent={activity.accent} size="lg" />
        <ChoiceRow gap={24}>
          {[upper, lowerChoice].map((value, i) => (
            <motion.div key={`${index}-${value}`} initial={reduceMotion ? false : { opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 360, damping: 22, delay: reduceMotion ? 0 : i * 0.08 }}>
              <ChoiceButton
                label={value}
                ariaLabel={i === 0 ? `Capital ${value}` : `Lowercase ${value}`}
                accent={activity.accent}
                size="calc(var(--tile, 72px) * 1.3)"
                onClick={() => pick(value)}
                wrong={isWrong(value)}
                correct={filled === value}
                dim={!!filled && filled !== value}
                reduceMotion={reduceMotion}
              />
            </motion.div>
          ))}
        </ChoiceRow>
      </Board>
    </GameFrame>
  );
};

/* ── Capital Where It Belongs ───────────────────────────────────────────── */
export const CapitalWhereGame = ({ activity, speak, sfx, profile, onComplete, onWrong, reduceMotion }) => {
  const name = sanitizedPracticeName(profile?.name || "") || "Pip";
  const finish = useCompleteOnce(onComplete);
  const [step, setStep] = useState(0);
  const [filled, setFilled] = useState("");
  const steps = [
    { target: "I", before: "", after: " like dogs.", mode: "letter", caption: "Capital I stands tall" },
    { target: name, before: "", after: " is here.", mode: "word", caption: "A name starts big" },
  ];
  const current = steps[step];

  const accepted = () => {
    setFilled(current.target);
    speak?.(`${current.target}${current.after}`, { interrupt: true });
    window.setTimeout(() => {
      setFilled("");
      if (step + 1 >= steps.length) finish();
      else setStep(step + 1);
    }, reduceMotion ? 200 : 900);
  };

  return (
    <GameFrame>
      <RoundBar
        left={<SentenceFrame before={current.before} after={current.after} filled={filled} accent={activity.accent} blankWidth={current.mode === "word" ? "3.4em" : "1em"} />}
        right={<RoundCounter done={step} total={steps.length} accent={activity.accent} />}
      />
      <WritingHandwritingPad
        key={step}
        target={current.target}
        accent={activity.accent}
        showsGuide={false}
        mode={current.mode}
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={accepted}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Name Starts Big ────────────────────────────────────────────────────── */
export const NameStartsBigGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [name, setName] = useState(null);

  if (!name) {
    return (
      <GameFrame center>
        <Board title={activity.title} symbol={activity.symbol} accent={activity.accent} caption="Pick a name to write">
          <ChoiceRow gap={16}>
            {WRITING_L6.nameSuggestions.map((item, i) => (
              <motion.div key={item} initial={reduceMotion ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 380, damping: 22, delay: reduceMotion ? 0 : i * 0.06 }}>
                <ChoiceButton
                  label={item}
                  ariaLabel={`Choose ${item}`}
                  accent={activity.accent}
                  size="calc(var(--tile, 72px) * 1.35)"
                  onClick={() => {
                    sfx?.("pop", { volume: 0.5 });
                    setName(item);
                    speak?.(`${item}. Write ${item} with a big first letter.`, { interrupt: true });
                  }}
                  reduceMotion={reduceMotion}
                />
              </motion.div>
            ))}
          </ChoiceRow>
        </Board>
      </GameFrame>
    );
  }

  return (
    <GameFrame>
      <RoundBar
        left={<TargetChip glyph={name} caption="Write it with a capital" accent={activity.accent} />}
        right={
          <IconButton label="Pick a different name" onClick={() => setName(null)} accent={activity.accent}>
            <PlayIcon size={20} />
          </IconButton>
        }
      />
      <WritingHandwritingPad
        target={name}
        accent={activity.accent}
        showsGuide={false}
        mode="word"
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={finish}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Fix the Start ──────────────────────────────────────────────────────── */
export const FixTheStartGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [tapped, setTapped] = useState(false);
  const [fixed, setFixed] = useState(false);
  const { flash, isWrong } = useWrongFlash();
  const round = WRITING_L6.repairRounds[index];
  const characters = round.incorrectSentence.split("");
  const firstIndex = characters.findIndex((c) => /[a-z]/i.test(c));

  const tapLetter = (i) => {
    if (tapped) return;
    if (i !== firstIndex) {
      flash(i);
      onWrong?.();
      speak?.("Look for the very first letter of the sentence.", { interrupt: true });
      return;
    }
    sfx?.("correct");
    setTapped(true);
    speak?.(`That is the first letter. Now write capital ${round.capital}.`, { interrupt: true });
  };

  const accepted = () => {
    setFixed(true);
    speak?.(round.correctedSentence, { interrupt: true });
    window.setTimeout(() => {
      if (index + 1 >= WRITING_L6.repairRounds.length) finish();
      else {
        setIndex((n) => n + 1);
        setTapped(false);
        setFixed(false);
      }
    }, reduceMotion ? 250 : 1100);
  };

  return (
    <GameFrame center={!tapped}>
      <RoundBar
        left={<span style={{ fontWeight: 700, fontSize: 17, color: LEARN.color.muted }}>{tapped ? "Rewrite it big" : "Tap the first letter"}</span>}
        right={<RoundCounter done={index} total={WRITING_L6.repairRounds.length} accent={activity.accent} />}
      />
      <ChoiceRow gap={6} style={{ padding: "6px 0" }}>
        {characters.map((ch, i) => (
          ch === " " ? (
            <span key={`sp-${i}`} style={{ width: "calc(var(--tile, 72px) * 0.3)" }} />
          ) : (
            <StickerTile
              key={`${index}-${i}`}
              size="calc(var(--tile, 72px) * 0.62)"
              accent={activity.accent}
              onClick={!tapped ? () => tapLetter(i) : undefined}
              disabled={tapped}
              ariaLabel={/[a-z]/i.test(ch) ? `Letter ${ch}` : ch}
              wrong={isWrong(i)}
              correct={tapped && i === firstIndex}
              selected={fixed && i === firstIndex}
              reduceMotion={reduceMotion}
              style={{ minWidth: 40, minHeight: 40 }}
            >
              <span style={{ fontSize: "calc(var(--tile, 72px) * 0.32)", lineHeight: 1 }}>{fixed && i === firstIndex ? round.capital : ch}</span>
            </StickerTile>
          )
        ))}
      </ChoiceRow>
      {tapped && (
        <WritingHandwritingPad
          key={round.capital}
          target={round.capital}
          accent={activity.accent}
          showsGuide={false}
          sfx={sfx}
          reduceMotion={reduceMotion}
          onAccepted={accepted}
          onRejected={() => onWrong?.()}
        />
      )}
    </GameFrame>
  );
};
